import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Radar, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { todayLong } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/common";
import { ScorePill, ValueRange } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";

function Count({ label, value, testId, accent }) {
  return (
    <div data-testid={testId} className="min-w-0">
      <div className={cn("font-display text-[24px] font-semibold leading-none tabular tracking-[-0.03em]", accent && "text-primary")}>{value}</div>
      <div className="mt-1 text-[11.5px] leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}

/** "You have been pre-researched" panel. Desktop right column; collapsible on mobile. */
export function MorningBrief({ brief, feed = [], className }) {
  const navigate = useNavigate();
  const intel = useIntel();
  if (!brief) {
    return (
      <div className={cn("surface-flat p-5", className)}><Skeleton className="h-5 w-1/2" /><Skeleton className="mt-3 h-3 w-3/4" /><div className="mt-5 grid grid-cols-2 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div></div>
    );
  }
  const top = brief.top?.[0];
  const topView = top ? feed.find((v) => v.project_id === top.project_id) : null;
  return (
    <aside className={cn("surface-flat relative overflow-hidden", className)} data-testid="today-morning-brief-panel">
      <div className="hero-wash px-5 pb-4 pt-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> Morning brief</div>
        <h2 className="font-display mt-1.5 text-[20px] font-semibold tracking-[-0.02em]" data-testid="today-morning-brief-greeting">{brief.greeting}</h2>
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">{todayLong()} · the market has been scanned for you.</p>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-5 border-t border-black/[0.06] px-5 py-5">
        <Count label="high-priority opportunities" value={brief.high_priority} testId="today-brief-high-priority" accent />
        <Count label="new signals last 7 days" value={brief.new_signals_7d} testId="today-brief-new-signals" />
        <Count label="follow-ups due" value={brief.follow_ups} testId="today-brief-follow-ups" />
        <Count label="expansion plays on customers" value={brief.expansion_count} testId="today-brief-expansion" />
      </div>
      <div className="border-t border-black/[0.06] px-5 py-4">
        <div className="eyebrow text-[10px]">Potential pipeline detected</div>
        <div className="mt-1 text-[20px]"><ValueRange label={brief.pipeline_label} testId="today-brief-pipeline" /></div>
        <div className="text-[11.5px] text-muted-foreground">Across high-priority new-business projects · estimates, not quotes</div>
      </div>
      {top ? (
        <div className="border-t border-black/[0.06] px-5 py-4" data-testid="today-brief-top">
          <div className="flex items-center justify-between gap-2">
            <div className="eyebrow text-[10px]">Highest priority</div>
            <ScorePill score={top.score} />
          </div>
          <div className="mt-2 text-[14px] font-semibold tracking-[-0.01em]">{top.company_name}</div>
          <div className="text-[13px] leading-snug text-foreground/85">{top.title}</div>
          <div className="mt-1 text-[12px] text-muted-foreground"><span className="font-mono tabular">{top.value_label}</span> · {top.action}</div>
          {brief.top.length > 1 ? (
            <div className="mt-3 space-y-1.5 border-t border-dashed border-black/[0.08] pt-3">
              {brief.top.slice(1).map((t) => (
                <button key={t.project_id} type="button" onClick={() => { const fv = feed.find((v) => v.project_id === t.project_id); if (fv) intel.openSignal(fv); else navigate(`/intel/companies/${t.company_id}`); }} className="flex w-full items-center justify-between gap-2 rounded-md text-left text-[12.5px] hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="today-brief-runner-up">
                  <span className="truncate"><span className="font-medium">{t.company_name}</span> <span className="text-muted-foreground">· {t.title}</span></span>
                  <span className="shrink-0 font-mono text-[11px] tabular text-muted-foreground">{t.score}</span>
                </button>
              ))}
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => (topView ? intel.openSignal(topView) : navigate(`/intel/companies/${top.company_id}`))} data-testid="today-morning-brief-review-top-button">Review top opportunity <ArrowRight className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/intel/radar")} data-testid="today-morning-brief-open-radar-button"><Radar className="h-3.5 w-3.5" /> Open radar</Button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
